import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { client } from '../api/client'
import { CoParentForm } from '../components/CoParentForm'
import { NavigationBar } from '../components/NavigationBar'
import { useAuthStore } from '../stores/auth'
import { Parent } from '../types'

interface PhoneData {
  id?: number
  number: string
  phone_type: string
}

interface EditProfileForm {
  first_name: string
  last_name: string
  street_name: string
  street_number: string
  city: string
  zip_code: string
  vat_number: string
  social_security_number: string
}

export function EditProfilePage() {
  const { t } = useTranslation(['home', 'common', 'profile'])
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { parent, updateParent } = useAuthStore()
  const [editError, setEditError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [phones, setPhones] = useState<PhoneData[]>([])
  const [showCoParentForm, setShowCoParentForm] = useState(false)
  const [highlightCoparentSsn, setHighlightCoparentSsn] = useState(false)

  // Check URL parameters and session storage for highlight flag
  useEffect(() => {
    const shouldHighlight = searchParams.get('highlightCoparentSsn') === 'true' || 
                           sessionStorage.getItem('highlightSSN') === 'true';
    const targetParentId = sessionStorage.getItem('targetParentId');
    
    // Only highlight if we have a target parent ID and it matches the co-parent's ID
    if (shouldHighlight && targetParentId && parent?.co_parent?.id === parseInt(targetParentId)) {
      setHighlightCoparentSsn(true);
      // Clear the session storage values after setting the highlight
      sessionStorage.removeItem('highlightSSN');
      sessionStorage.removeItem('targetParentId');
    }
  }, [searchParams, parent?.co_parent?.id])

  // Form setup for edit profile
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
  } = useForm<EditProfileForm>()

  // Set isLoading to false when parent data is available
  useEffect(() => {
    if (parent) {
      setIsLoading(false)
      
      // Verify co-parent existence if one is attached to the parent
      if (parent.co_parent) {
        verifyCoParentExists(parent.co_parent.id);
      }
    }
  }, [parent])

  // Function to verify if the co-parent still exists in the backend
  const verifyCoParentExists = async (coParentId: number) => {
    try {
      await client.get(`/co-parents/${coParentId}/`);
      // If the request succeeds, the co-parent exists
    } catch (error: any) {
      // If we get a 404, the co-parent doesn't exist anymore
      if (error.response && error.response.status === 404) {
        // Update parent state to remove the co-parent
        if (parent) {
          updateParent({
            ...parent,
            co_parent: null
          });
        }
      }
    }
  }

  // Function to refresh profile data from the backend
  const refreshProfileData = async () => {
    try {
      setIsLoading(true);
      const response = await client.get('/profile/');
      updateParent(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error refreshing profile data:', error);
      setIsLoading(false);
    }
  }

  // Set initial form values when data is loaded
  useEffect(() => {
    if (!isLoading && parent) {
      reset({
        first_name: parent.first_name || '',
        last_name: parent.last_name || '',
        street_name: parent.street_name || '',
        street_number: parent.street_number || '',
        city: parent.city || '',
        zip_code: parent.zip_code || '',
        vat_number: parent.vat_number || '',
        social_security_number: parent.social_security_number || '',
      })
      
      // Set phones if available
      if (parent.phones && parent.phones.length > 0) {
        setPhones(parent.phones)
      }
    }
  }, [isLoading, parent, reset])

  const addPhone = () => {
    if (phones.length < 5) {
      setPhones([...phones, { number: '', phone_type: 'mobile' }])
    } else {
      setEditError(t('validation.maxPhones', { ns: 'profile' }))
    }
  }

  const removePhone = (index: number) => {
    if (phones.length > 1) {
      setPhones(phones.filter((_, i) => i !== index))
    }
  }

  const updatePhoneType = (index: number, type: string) => {
    const newPhones = [...phones]
    newPhones[index].phone_type = type
    setPhones(newPhones)
  }

  const updatePhoneNumber = (index: number, number: string) => {
    const newPhones = [...phones]
    newPhones[index].number = number
    setPhones(newPhones)
  }

  const onSubmit = async (data: EditProfileForm) => {
    setIsSubmitting(true)
    setEditError(null)

    // Prepare the data to send
    const profileData = {
      first_name: data.first_name,
      last_name: data.last_name,
      street_name: data.street_name,
      street_number: data.street_number,
      city: data.city,
      zip_code: data.zip_code,
      vat_number: data.vat_number,
      social_security_number: data.social_security_number,
      phones: phones
    }

    try {
      // Validate that at least one mobile phone exists
      const hasMobilePhone = phones.some(phone => phone.phone_type === 'mobile')
      if (!hasMobilePhone) {
        setEditError(t('validation.mobileRequired', { ns: 'profile' }))
        setIsSubmitting(false)
        return
      }

      // Validate that there are no more than 5 phones
      if (phones.length > 5) {
        setEditError(t('validation.maxPhones', { ns: 'profile' }))
        setIsSubmitting(false)
        return
      }
      
      // Validate that no phone number is empty
      if (phones.some(phone => !phone.number || phone.number.trim() === '')) {
        setEditError(t('validation.phoneNumberEmpty', { ns: 'profile' }))
        setIsSubmitting(false)
        return
      }

      const response = await client.patch('/profile/', profileData)
      updateParent(response.data)
      navigate('/')
    } catch (error: unknown) {
      // Handle specific validation errors
      const err = error as any; // Type assertion for backward compatibility
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        
        // Handle VAT number errors
        if (errorData.vat_number) {
          if (errorData.vat_number[0].includes('9 digits')) {
            setError('vat_number', { 
              type: 'manual', 
              message: t('validation.vatFormat', { ns: 'profile' }) 
            });
          } else if (errorData.vat_number[0].includes('check digit')) {
            setError('vat_number', { 
              type: 'manual', 
              message: t('validation.vatInvalid', { ns: 'profile' }) 
            });
          } else if (errorData.vat_number[0].includes('already in use')) {
            setError('vat_number', { 
              type: 'manual', 
              message: t('validation.vatExists', { ns: 'profile' }) 
            });
          }
        }
        
        // Handle Social Security Number errors
        if (errorData.social_security_number) {
          if (errorData.social_security_number[0].includes('11 digits')) {
            setError('social_security_number', { 
              type: 'manual', 
              message: t('validation.ssnFormat', { ns: 'profile' }) 
            });
          } else if (errorData.social_security_number[0].includes('already in use')) {
            setError('social_security_number', { 
              type: 'manual', 
              message: t('validation.ssnExists', { ns: 'profile' }) 
            });
          }
        }
        
        setEditError(t('errors.updateFailed', { ns: 'profile' }))
      } else {
        setEditError(t('errors.updateFailed', { ns: 'profile' }))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle cancel button click - navigate without saving changes
  const handleCancel = () => {
    navigate('/')
  }

  const handleAddCoParent = () => {
    setShowCoParentForm(true)
  }

  const handleCoParentSuccess = (newCoParent: Parent) => {
    setShowCoParentForm(false)
    
    // Update parent with co-parent information
    if (parent) {
      updateParent({
        ...parent,
        co_parent: newCoParent
      })
    }
  }

  const handleCoParentRemove = () => {
    // Update parent with no co-parent
    if (parent) {
      updateParent({
        ...parent,
        co_parent: null
      })
    }
  }

  return (
    <div className="edit-profile-container" style={{ minHeight: '100vh' }}>
      {/* Navigation Bar */}
      <NavigationBar />

      {/* Main Content */}
      <div className="max-w-2xl mx-auto py-12 px-4">
        <div className="card-neobrutalism">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2 text-neobrutalism-text" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <h1 className="text-2xl font-black text-neobrutalism-text">
                {t('profile.edit.title')}
              </h1>
            </div>
          </div>

          {editError && editError !== t('validation.mobileRequired', { ns: 'profile' }) && 
                        editError !== t('validation.maxPhones', { ns: 'profile' }) && (
            <div className="bg-neobrutalism-red p-4 rounded-lg border-2 border-black mb-4">
              <p className="text-neobrutalism-text font-bold">{editError}</p>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-neobrutalism-accent"></div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div className="mb-4">
                  <label className="block font-bold mb-2" htmlFor="first_name">
                    {t('profile.edit.firstName', { ns: 'profile' })}
                    <span className="text-gray-500 text-sm italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    {...register('first_name', { required: t('profile.edit.firstNameRequired', { ns: 'profile' }) })}
                    className="w-full p-2 border-2 border-black"
                  />
                  {errors.first_name && (
                    <p className="mt-1 text-neobrutalism-red font-bold">{errors.first_name.message}</p>
                  )}
                </div>

                {/* Last Name */}
                <div className="mb-4">
                  <label className="block font-bold mb-2" htmlFor="last_name">
                    {t('profile.edit.lastName', { ns: 'profile' })}
                    <span className="text-gray-500 text-sm italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    {...register('last_name', { required: t('profile.edit.lastNameRequired', { ns: 'profile' }) })}
                    className="w-full p-2 border-2 border-black"
                  />
                  {errors.last_name && (
                    <p className="mt-1 text-neobrutalism-red font-bold">{errors.last_name.message}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="mb-4">
                <label className="block font-bold mb-2" htmlFor="email">
                  {t('profile.edit.email', { ns: 'profile' })}
                  <span className="text-gray-500 text-sm italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                </label>
                <input
                  type="email"
                  id="email"
                  value={parent?.email || ''}
                  disabled
                  className="w-full p-2 border-2 border-gray-300 bg-gray-100"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Social Security Number */}
                <div className="mb-4">
                  <label className="block font-bold mb-2" htmlFor="social_security_number">
                    {t('fields.socialSecurityNumber', { ns: 'profile' })}
                    <span className="text-gray-500 text-sm italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                  </label>
                  <input
                    type="text"
                    id="social_security_number"
                    {...register('social_security_number', { 
                      required: parent?.skip_checks ? false : t('validation.ssnRequired', { ns: 'profile' }),
                      pattern: {
                        value: /^\d{11}$/,
                        message: t('validation.ssnFormat', { ns: 'profile' })
                      }
                    })}
                    className="w-full p-2 border-2 border-black"
                    placeholder={t('fields.socialSecurityNumberPlaceholder', { ns: 'profile' })}
                  />
                  {errors.social_security_number && (
                    <p className="mt-1 text-neobrutalism-red font-bold">{errors.social_security_number.message}</p>
                  )}
                </div>

                {/* VAT Number */}
                <div className="mb-4">
                  <label className="block font-bold mb-2" htmlFor="vat_number">
                    {t('fields.vatNumber', { ns: 'profile' })}
                    <span className="text-gray-500 text-sm italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                  </label>
                  <input
                    type="text"
                    id="vat_number"
                    {...register('vat_number', { 
                      required: parent?.skip_checks ? false : t('validation.vatRequired', { ns: 'profile' }),
                      pattern: {
                        value: /^\d{9}$/,
                        message: t('validation.vatFormat', { ns: 'profile' })
                      }
                    })}
                    className="w-full p-2 border-2 border-black"
                    placeholder={t('fields.vatNumberPlaceholder', { ns: 'profile' })}
                  />
                  {errors.vat_number && (
                    <p className="mt-1 text-neobrutalism-red font-bold">{errors.vat_number.message}</p>
                  )}
                </div>
              </div>

              {/* Address Section Header */}
              <div className="mt-8 mb-4">
                <h3 className="text-xl font-bold">{t('profile.edit.addressSection', { ns: 'profile' })}
                  <span className="text-gray-500 text-sm italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                </h3>
              </div>

              {/* Address Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Street Name */}
                <div className="mb-4">
                  <label className="block font-bold mb-2" htmlFor="street_name">
                    {t('fields.streetName', { ns: 'profile' })}
                  </label>
                  <input
                    type="text"
                    id="street_name"
                    {...register('street_name', { required: t('validation.streetNameRequired', { ns: 'profile' }) })}
                    className="w-full p-2 border-2 border-black"
                    placeholder={t('fields.streetNamePlaceholder', { ns: 'profile' })}
                  />
                  {errors.street_name && (
                    <p className="mt-1 text-neobrutalism-red font-bold">{errors.street_name.message}</p>
                  )}
                </div>
                
                {/* Street Number */}
                <div className="mb-4">
                  <label className="block font-bold mb-2" htmlFor="street_number">
                    {t('fields.streetNumber', { ns: 'profile' })}
                  </label>
                  <input
                    type="text"
                    id="street_number"
                    {...register('street_number', { required: t('validation.streetNumberRequired', { ns: 'profile' }) })}
                    className="w-full p-2 border-2 border-black"
                    placeholder={t('fields.streetNumberPlaceholder', { ns: 'profile' })}
                  />
                  {errors.street_number && (
                    <p className="mt-1 text-neobrutalism-red font-bold">{errors.street_number.message}</p>
                  )}
                </div>
                
                {/* City */}
                <div className="mb-4">
                  <label className="block font-bold mb-2" htmlFor="city">
                    {t('fields.city', { ns: 'profile' })}
                  </label>
                  <input
                    type="text"
                    id="city"
                    {...register('city', { required: t('validation.cityRequired', { ns: 'profile' }) })}
                    className="w-full p-2 border-2 border-black"
                    placeholder={t('fields.cityPlaceholder', { ns: 'profile' })}
                  />
                  {errors.city && (
                    <p className="mt-1 text-neobrutalism-red font-bold">{errors.city.message}</p>
                  )}
                </div>
                
                {/* Zip Code */}
                <div className="mb-4">
                  <label className="block font-bold mb-2" htmlFor="zip_code">
                    {t('fields.zipCode', { ns: 'profile' })}
                  </label>
                  <input
                    type="text"
                    id="zip_code"
                    {...register('zip_code', { required: t('validation.zipCodeRequired', { ns: 'profile' }) })}
                    className="w-full p-2 border-2 border-black"
                    placeholder={t('fields.zipCodePlaceholder', { ns: 'profile' })}
                  />
                  {errors.zip_code && (
                    <p className="mt-1 text-neobrutalism-red font-bold">{errors.zip_code.message}</p>
                  )}
                </div>
              </div>

              {/* Phone Numbers Section */}
              <div className="md:col-span-2 space-y-4 mt-8">
                <div className="flex justify-between items-center">
                  <label className="block font-bold">
                    {t('fields.phoneNumbers', { ns: 'profile' })}
                    <span className="text-gray-500 text-sm italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                  </label>
                  <button
                    type="button"
                    onClick={addPhone}
                    disabled={phones.length >= 5}
                    className={`text-sm font-bold ${phones.length >= 5 ? 'text-gray-400 cursor-not-allowed' : 'text-neobrutalism-primary hover:text-neobrutalism-primary-dark'}`}
                  >
                    + {t('fields.addPhone', { ns: 'profile' })}
                  </button>
                </div>
                
                {phones.map((phone, index) => (
                  <div key={index} className="flex gap-2">
                    <select
                      value={phone.phone_type}
                      onChange={(e) => updatePhoneType(index, e.target.value)}
                      className="p-2 border-2 border-black min-w-[120px] w-[120px]"
                    >
                      <option value="mobile">{t('fields.mobile', { ns: 'profile' })}</option>
                      <option value="home">{t('fields.home', { ns: 'profile' })}</option>
                      <option value="work">{t('fields.work', { ns: 'profile' })}</option>
                      <option value="other">{t('fields.other', { ns: 'profile' })}</option>
                    </select>
                    <input
                      type="text"
                      value={phone.number}
                      onChange={(e) => updatePhoneNumber(index, e.target.value)}
                      placeholder={t('fields.phoneNumberPlaceholder', { ns: 'profile' })}
                      className={`flex-1 p-2 border-2 ${!phone.number || phone.number.trim() === '' ? 'border-red-500' : 'border-black'}`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => removePhone(index)}
                      className="text-red-600 hover:text-red-800"
                      disabled={phones.length <= 1}
                      title={phones.length <= 1 ? t('validation.atLeastOnePhone', { ns: 'profile' }) : ''}
                    >
                      ×
                    </button>
                  </div>
                ))}
                
                {/* Phone-related error messages */}
                {phones.some(phone => !phone.number || phone.number.trim() === '') && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{t('validation.phoneNumberEmpty', { ns: 'profile' })}</p>
                )}
                {editError && (editError === t('validation.mobileRequired', { ns: 'profile' }) || 
                              editError === t('validation.maxPhones', { ns: 'profile' }) ||
                              editError === t('validation.phoneNumberEmpty', { ns: 'profile' })) && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{editError}</p>
                )}
              </div>

              {/* Co-Parent Section */}
              <div className="mt-8 mb-2">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold">{t('coParent.section.title', { ns: 'profile' })}</h3>
                  {!parent?.co_parent && !showCoParentForm && (
                    <button
                      type="button"
                      onClick={handleAddCoParent}
                      className="px-4 py-2 bg-yellow-400 border-2 border-black font-bold flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                      {t('coParent.actions.add', { ns: 'profile' })}
                    </button>
                  )}
                </div>
                
                {!showCoParentForm && !parent?.co_parent && (
                  <div className="bg-yellow-50 p-4 rounded-lg border-2 border-yellow-200 my-4 text-sm">
                    <div className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="mb-3">{t('coParent.info.description', { ns: 'profile' })}</p>
                        <p className="mb-3">
                          <span className="text-blue-600 mr-1">🔹</span>
                          <span className="font-semibold">{t('coParent.info.reminder', { ns: 'profile' })}</span>
                        </p>
                        <p>
                          <span className="text-red-600 mr-1">⚠️</span>
                          <span className="font-semibold">{t('coParent.info.important', { ns: 'profile' })}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {showCoParentForm && (
                <div>
                  <CoParentForm
                    onSuccess={handleCoParentSuccess}
                    onRemove={handleCoParentRemove}
                    existingCoParent={parent?.co_parent || null}
                    parent={parent}
                    isNested={true}
                    highlightSsn={highlightCoparentSsn}
                  />
                </div>
              )}

              {parent?.co_parent && !showCoParentForm && (
                <div>
                  <CoParentForm 
                    existingCoParent={parent.co_parent} 
                    onSuccess={handleCoParentSuccess} 
                    onRemove={handleCoParentRemove}
                    isNested={true}
                    parent={parent}
                    highlightSsn={highlightCoparentSsn}
                  />
                </div>
              )}

              {/* Form Actions */}
              <div className="flex space-x-4 mt-8">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-neobrutalism bg-neobrutalism-red flex-1 text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4"
                >
                  {t('common:buttons.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-neobrutalism bg-neobrutalism-accent flex-1 text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4"
                >
                  {isSubmitting ? t('common:buttons.saving') : t('common:buttons.save')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
} 